import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { apiFetch, ApiError, friendlyMessage } from '../../api/apiClient';
import { getToken, setToken, isTokenValid } from '../../auth/token';
import { safeNextPath } from '../../auth/AuthGuard';
import { InlineAlert } from '../../shared/components/status';

// ─── QcLogin ────────────────────────────────────────────────────────────────
// Sign-in for QC staff only. Deliberately plain -- nobody but the QC team
// ever sees this page, so it skips the branded styling the customer
// Signin page has. Posts to /api/qc/signin (see QcAuthController.java)
// and stores the token under 'qcToken', not 'token', so it can never be
// confused with (or silently overwrite) a customer session in the same
// browser (see src/auth/token.js).

const REASON_MESSAGES = {
  signin:  'Please sign in to continue.',
  expired: 'Your session has expired. Please sign in again.',
};

export default function QcLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = safeNextPath(searchParams.get('next'), '/qc');
  const [reasonMessage, setReasonMessage] = useState(REASON_MESSAGES[searchParams.get('reason')] || '');

  // Already signed in as QC? Skip the form.
  useEffect(() => {
    if (isTokenValid(getToken('qc'))) navigate(next, { replace: true });
  }, [navigate, next]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setReasonMessage('');
    setSubmitting(true);

    try {
      const data = await apiFetch('/api/qc/signin', {
        method: 'POST',
        auth: false,
        body: { username, password },
      });
      setToken(data.token, 'qc');
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Invalid username or password.');
      } else {
        setError(friendlyMessage(err, "We couldn't sign you in. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', fontFamily: 'sans-serif', background: '#f4f4f4',
    }}>
      <Box sx={{
        width: 320, background: '#fff', border: '1px solid #ccc', borderRadius: '4px',
        padding: '24px',
      }}>
        <Typography component="h1" sx={{ fontSize: '18px', fontWeight: 600, mb: 2 }}>
          Quality Control Login
        </Typography>

        {reasonMessage && <InlineAlert severity="info" sx={{ mb: 2 }}>{reasonMessage}</InlineAlert>}
        {error && <InlineAlert severity="error" sx={{ mb: 2 }}>{error}</InlineAlert>}

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Box>
            <Typography component="label" htmlFor="qc-username" sx={{ fontSize: '12px', mb: 0.5, display: 'block' }}>Username</Typography>
            <Box
              component="input"
              id="qc-username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              sx={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '3px', boxSizing: 'border-box' }}
            />
          </Box>
          <Box>
            <Typography component="label" htmlFor="qc-password" sx={{ fontSize: '12px', mb: 0.5, display: 'block' }}>Password</Typography>
            <Box
              component="input"
              id="qc-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '3px', boxSizing: 'border-box' }}
            />
          </Box>
          <Box
            component="button"
            type="submit"
            disabled={submitting}
            sx={{
              marginTop: '8px', padding: '10px', background: '#333', color: '#fff',
              border: 'none', borderRadius: '3px', cursor: submitting ? 'default' : 'pointer', fontSize: '14px',
            }}
          >
            {submitting ? 'Logging in…' : 'Log in'}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
